/**
 * The photo compare on a done task (docs/roadmap-2026-10.md, flag `compareUI`): the before and the
 * after the same size, one over the other, and a bronze handle the thumb drags to wipe between
 * them. The handle moves in hard 4px steps like a sprite, never eased, and starts in the middle.
 * Both viewers (src/components/CompareLightbox.tsx, client/components/CompareView.tsx) place it
 * through here.
 */

/** The handle's step: it lands only on multiples of this many pixels from the photo's left edge. */
export const COMPARE_STEP = 4;
/** An arrow key moves the handle this far (four steps). */
export const COMPARE_KEY_STEP = 16;

/**
 * The divider's position for a touch `x` pixels from the photo's left edge: the raw finger is
 * clamped to the photo first, then snapped to the step, so a finger past either edge pins the
 * handle there and it follows again the moment the finger comes back. Both ends are reachable
 * exactly: 0, and the full width even when the width is not a multiple of the step (the last step
 * is then shorter).
 */
export function compareSnap(x: number, width: number): number {
  if (!(width > 0)) return 0;
  const clamped = Math.max(0, Math.min(width, Number.isFinite(x) ? x : 0));
  const snapped = Math.round(clamped / COMPARE_STEP) * COMPARE_STEP;
  // Within half a step of the right edge, or snapped past it: the edge itself.
  if (snapped > width || width - clamped < COMPARE_STEP / 2) return width;
  return snapped;
}

/** Where the handle starts: the middle, on a step. */
export function compareStart(width: number): number {
  return compareSnap(width / 2, width);
}

/** The handle after a key: arrows move four steps, Home and End go to the edges. Other keys, null. */
export function compareKey(pos: number, width: number, key: string): number | null {
  if (key === "ArrowLeft" || key === "ArrowDown") return compareSnap(pos - COMPARE_KEY_STEP, width);
  if (key === "ArrowRight" || key === "ArrowUp") return compareSnap(pos + COMPARE_KEY_STEP, width);
  if (key === "Home") return 0;
  if (key === "End") return compareSnap(width, width);
  return null;
}

/** The handle's place as a whole percent of the before photo shown, for a screen reader. */
export function comparePercent(pos: number, width: number): number {
  return width > 0 ? Math.round((pos / width) * 100) : 50;
}

/** The control's words, the same on both clients. */
export const COMPARE_WORDS = {
  button: "Compare",
  slider: "Before and after",
  /** What a screen reader hears as the value: "60% before". */
  value: (percent: number) => `${percent}% before`,
} as const;
